import React, { FC, useMemo } from 'react'

import { Form, Formik } from 'formik'
import CreatableSelect from 'react-select/creatable'
import { toast } from 'react-toastify'

import TextEditorField from '@/components/FormikInputs/TextEditorField'
import { defaultDelayReasons } from '@/constants/delayReasons'
import { useCurrentApplication } from '@/graphql/services/application'
import { useUpdateApplicationMutation } from '@/services/application'
import { useCreateDelayReasonMutation, useDeleteDelayReasonMutation } from '@/services/delayReasons'
import { ChecklistGroup } from '@/types/unlock'
import { applicationStates, applicationStatesValues } from '@/utils/checklistGroupMappings'

type Option = { value: string; label: string }

const delayReasonsOptions: Option[] = defaultDelayReasons.map((reason) => ({
  value: reason,
  label: reason,
}))

export type DelayReasonSectionProps = {
  checklistGroup: ChecklistGroup
}

export const DelayReasonSection: FC<DelayReasonSectionProps> = (props) => {
  const { checklistGroup } = props
  const [createDelayReason] = useCreateDelayReasonMutation()
  const [deleteDelayReason] = useDeleteDelayReasonMutation()
  const [updateApplication] = useUpdateApplicationMutation()
  const { data: application } = useCurrentApplication()

  const reasons: Option[] = useMemo(
    () => {
      return application?.delayReasons
        ?.split(',')
        .map((v) => delayReasonsOptions.find((opt) => opt.label === v) || { value: v, label: v })
    },
    [application?.delayReasons] || []
  )

  const isGreaterOrEqualCondlApproval = useMemo(
    () =>
      application?.applicationStateIndex >=
      applicationStates?.find(
        (state) => state.application_state === applicationStatesValues.COND_APPROVAL
      )?.index,
    [application]
  )

  const content = useMemo(() => {
    return (
      <>
        {isGreaterOrEqualCondlApproval ? (
          <Formik
            enableReinitialize
            initialValues={{
              content: application?.opsDelayReasons || '',
            }}
            onSubmit={async (values: { content: string }) => {
              updateApplication({
                external_id: `${application.externalId}`,
                ops_delay_reasons: values.content,
              }).then((resp) => {
                if (resp['error']) {
                  toast.error(`Error during Ops Delay Reasons field update`, {
                    containerId: 'global',
                  })
                } else {
                  toast.success(`Ops Delay Reasons field updated!`, {
                    containerId: 'global',
                  })
                  return true
                }
              })
            }}
          >
            {({ submitForm, setFieldValue }) => (
              <Form className='font-normal'>
                <TextEditorField
                  onChangeApplied={submitForm}
                  placeholder='~'
                  setFieldValue={setFieldValue}
                  name='content'
                  disabled={!application}
                />
              </Form>
            )}
          </Formik>
        ) : (
          <CreatableSelect
            instanceId='delay-reason-select'
            isMulti={true}
            isClearable={false}
            options={delayReasonsOptions}
            value={reasons}
            onChange={(newValue) => {
              updateApplication({
                external_id: `${application.externalId}`,
                delay_reasons: newValue.map((it) => it.label).join(', '),
              }).then((resp) => {
                if (resp['error']) {
                  toast.error(`Error during Sales Delay Reasons field update`, {
                    containerId: 'global',
                  })
                } else {
                  toast.success(`Ops Sales Reasons field updated!`, {
                    containerId: 'global',
                  })
                  return true
                }
              })
            }}
          />
        )}
      </>
    )
  }, [checklistGroup, createDelayReason, deleteDelayReason, reasons, application])

  return content ? (
    <div className='px-2 py-4'>
      <p className='font-bold text-sm mb-1'>
        {isGreaterOrEqualCondlApproval ? 'Ops' : 'Sales'} Delay Reasons
      </p>
      {content}
    </div>
  ) : null
}
